import { apiClient } from './client';

// ── Types ────────────────────────────────────────────────────────────

export interface IntakeBatchSummary {
  id: string;
  file_name: string;
  source_type: string;
  data_type: string;
  detected_format: string | null;
  status: string;
  row_count: number | null;
  error_count: number;
  warning_count: number;
  site_id: string | null;
  site_name: string | null;
  uploaded_by: string;
  uploaded_by_email: string | null;
  created_at: string;
  reviewed_at: string | null;
  committed_at: string | null;
  // Shared across every sheet/batch created from a single upload, so the UI can
  // collapse a multi-sheet workbook into one ingestion row. Null for legacy rows.
  upload_group_id?: string | null;
}

export interface IntakeRowDetail {
  id: string;
  row_number: number;
  status: string;
  raw_data: Record<string, string>;
  mapped_data: Record<string, string> | null;
  validation_errors: Array<{
    rule: string;
    severity: string;
    message: string;
    field?: string;
    value?: string | number;
  }> | null;
  excluded_reason: string | null;
}

export interface ValidationSummary {
  total_rows: number;
  valid_rows: number;
  warning_rows: number;
  error_rows: number;
  errors: Array<{ rule: string; count: number }>;
  warnings: Array<{ rule: string; count: number }>;
}

export interface IntakeBatchDetail {
  id: string;
  file_name: string;
  source_type: string;
  data_type: string;
  detected_format: string | null;
  status: string;
  row_count: number | null;
  error_count: number;
  warning_count: number;
  site_id: string | null;
  site_name: string | null;
  column_mapping: Record<string, string> | null;
  uploaded_by: string;
  uploaded_by_email: string | null;
  created_at: string;
  reviewed_at: string | null;
  review_notes: string | null;
  committed_at: string | null;
}

// A sibling sheet from the same upload (shared upload_group_id) — drives the
// sheet-selector tabs on the batch detail page.
export interface IntakeBatchSibling {
  id: string;
  sheet_label: string;
  data_type: string;
  sheet_role: string | null;
  status: string;
  row_count: number | null;
  is_current: boolean;
}

export interface AuditEntry {
  id: string;
  action: string;
  performed_by: string;
  performed_by_email: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

// One detected sheet within a multi-sheet preview. Single-sheet/CSV uploads
// One ranked candidate field for a column, from the deterministic tiers or the
// LLM fallback. `source` distinguishes them; `reason` is the LLM's one-liner.
export interface ColumnCandidate {
  field: string;
  confidence: number;
  reason: string;
  source: 'alias' | 'semantic' | 'fuzzy' | 'llm';
}

export interface ColumnSuggestion {
  mapped_to: string | null;
  confidence: number;
  source: 'alias' | 'semantic' | 'fuzzy' | 'llm' | 'unmapped';
  candidates: ColumnCandidate[];
}

// Per-column AI suggestions keyed by header. Powers the confidence-band review.
export type ColumnSuggestions = Record<string, ColumnSuggestion>;

// Sheet structural role: only `primary_data` sheets are ingested; qc_data /
// lookup / metadata sheets are set aside (a QC blank must never become a result).
export type SheetRole =
  | 'primary_data' | 'qc_data' | 'lookup' | 'metadata' | 'empty' | 'unknown';

// flatten into the legacy UploadPreview shape (sheet_name omitted).
export interface SheetPreview {
  sheet_name: string | null;
  data_type: string;
  detection_source: 'sheet_name' | 'headers' | 'ambiguous' | 'explicit' | 'default';
  sheet_role?: SheetRole;
  ingestible?: boolean;
  headers: string[];
  column_mapping: Record<string, string>;
  column_suggestions?: ColumnSuggestions;
  detected_format: string;
  row_count: number;
  preview_rows: Record<string, string>[];
  matched_template?: {
    matched_template_id: string;
    matched_template_name: string;
    matched_template_lab: string | null;
    match_score: number;
  };
}

// Per-sheet column overrides chosen in the review UI, keyed by sheet name
// ('' for single-sheet/CSV uploads): { sheetName: { header: field | '' } }.
export type MappingOverrides = Record<string, Record<string, string>>;

// Legacy single-sheet shape — preserved so existing single-CSV uploads
// don't break the IntakePage preview UI.
export interface UploadPreview {
  preview: boolean;
  file_name: string;
  headers: string[];
  column_mapping: Record<string, string>;
  column_suggestions?: ColumnSuggestions;
  sheet_role?: SheetRole;
  ingestible?: boolean;
  detected_format: string;
  row_count: number;
  preview_rows: Record<string, string>[];
  matched_template?: {
    matched_template_id: string;
    matched_template_name: string;
    matched_template_lab: string | null;
    match_score: number;
  };
  // Multi-sheet shape (Talon canonical template). When `multi_sheet=true`,
  // `sheets` holds one entry per detected tab; the legacy fields above are
  // empty/zero-valued.
  multi_sheet?: boolean;
  sheets?: SheetPreview[];
}

// Result of uploadIntakeFile — either a single batch summary (legacy) or a
// list of batches (multi-sheet upload, one batch per detected tab).
export type UploadResult = IntakeBatchSummary | { batches: IntakeBatchSummary[] };

// ── API Functions ────────────────────────────────────────────────────

export async function previewIntakeFile(
  file: File,
  siteId?: string,
  dataType?: string,
): Promise<UploadPreview> {
  const form = new FormData();
  form.append('file', file);
  if (siteId) form.append('site_id', siteId);
  if (dataType) form.append('data_type', dataType);
  const { data } = await apiClient.post('/data-vault/intake/upload?preview=true', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function uploadIntakeFile(
  file: File,
  siteId?: string,
  dataType?: string,
  mappingOverrides?: MappingOverrides,
): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file);
  if (siteId) form.append('site_id', siteId);
  if (dataType) form.append('data_type', dataType);
  if (mappingOverrides && Object.keys(mappingOverrides).length > 0) {
    form.append('mapping_overrides', JSON.stringify(mappingOverrides));
  }
  const { data } = await apiClient.post('/data-vault/intake/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function listIntakeBatches(params: {
  status?: string;
  site_id?: string;
  page?: number;
  page_size?: number;
}): Promise<{ batches: IntakeBatchSummary[]; total: number }> {
  const { data } = await apiClient.get('/data-vault/intake/batches', { params });
  return data;
}

export async function getIntakeBatchDetail(
  batchId: string,
): Promise<{ batch: IntakeBatchDetail; validation_summary: ValidationSummary | null; siblings: IntakeBatchSibling[] }> {
  const { data } = await apiClient.get(`/data-vault/intake/batches/${batchId}`);
  return data;
}

export async function getIntakeBatchRows(
  batchId: string,
  params: { status?: string; page?: number; page_size?: number },
): Promise<{ rows: IntakeRowDetail[]; total: number }> {
  const { data } = await apiClient.get(`/data-vault/intake/batches/${batchId}/rows`, { params });
  return data;
}

export async function updateIntakeRow(
  batchId: string,
  rowId: string,
  updates: Record<string, string>,
): Promise<void> {
  await apiClient.patch(`/data-vault/intake/batches/${batchId}/rows/${rowId}`, updates);
}

export async function excludeIntakeRow(
  batchId: string,
  rowId: string,
  reason: string,
): Promise<void> {
  const form = new FormData();
  form.append('reason', reason);
  await apiClient.post(`/data-vault/intake/batches/${batchId}/rows/${rowId}/exclude`, form);
}

export async function revalidateIntakeBatch(
  batchId: string,
): Promise<{ status: string; total_rows: number; valid_rows: number; warning_rows: number; error_rows: number }> {
  const { data } = await apiClient.post(`/data-vault/intake/batches/${batchId}/validate`);
  return data;
}

export async function approveIntakeBatch(
  batchId: string,
  notes: string = '',
): Promise<{ status: string; committed_rows: number }> {
  const form = new FormData();
  form.append('notes', notes);
  const { data } = await apiClient.post(`/data-vault/intake/batches/${batchId}/approve`, form);
  return data;
}

// Two-actor gate (#4): qa-pass (sign-off, no commit) then commit (to production).
export async function qaPassIntakeBatch(
  batchId: string,
  notes: string = '',
): Promise<{ status: string }> {
  const form = new FormData();
  form.append('notes', notes);
  const { data } = await apiClient.post(`/data-vault/intake/batches/${batchId}/qa-pass`, form);
  return data;
}

export async function commitIntakeBatch(
  batchId: string,
): Promise<{ status: string; committed_rows: number; skipped_rows?: number }> {
  const { data } = await apiClient.post(`/data-vault/intake/batches/${batchId}/commit`);
  return data;
}

export async function rejectIntakeBatch(
  batchId: string,
  reason: string = '',
): Promise<void> {
  const form = new FormData();
  form.append('reason', reason);
  await apiClient.post(`/data-vault/intake/batches/${batchId}/reject`, form);
}

export async function rollbackIntakeBatch(
  batchId: string,
  reason: string = '',
): Promise<void> {
  const form = new FormData();
  form.append('reason', reason);
  await apiClient.post(`/data-vault/intake/batches/${batchId}/rollback`, form);
}

export async function bulkExcludeRows(
  batchId: string,
  statusFilter: string = 'error',
  reason: string = 'Bulk excluded',
): Promise<{ excluded_count: number }> {
  const form = new FormData();
  form.append('status_filter', statusFilter);
  form.append('reason', reason);
  const { data } = await apiClient.post(`/data-vault/intake/batches/${batchId}/rows/bulk-exclude`, form);
  return data;
}

export async function bulkFindReplace(
  batchId: string,
  field: string,
  findValue: string,
  replaceValue: string,
  matchExact: boolean = true,
): Promise<{ affected_count: number }> {
  const { data } = await apiClient.post(`/data-vault/intake/batches/${batchId}/find-replace`, {
    field,
    find_value: findValue,
    replace_value: replaceValue,
    match_exact: matchExact,
  });
  return data;
}

export async function deleteIntakeBatch(batchId: string): Promise<void> {
  await apiClient.delete(`/data-vault/intake/batches/${batchId}`);
}

export async function remapBatchColumns(
  batchId: string,
  mapping: Record<string, string>,
): Promise<{ batch: IntakeBatchDetail; validation_summary: ValidationSummary | null }> {
  const { data } = await apiClient.put(`/data-vault/intake/batches/${batchId}/remap`, { mapping });
  return data;
}

export async function getIntakeBatchAudit(batchId: string): Promise<AuditEntry[]> {
  const { data } = await apiClient.get(`/data-vault/intake/batches/${batchId}/audit`);
  return data;
}

export async function getWellSuggestions(batchId: string): Promise<{
  suggestions: Array<{
    unrecognized: string;
    candidates: Array<{ well_name: string; well_id: string }>;
  }>;
}> {
  const { data } = await apiClient.get(`/data-vault/intake/batches/${batchId}/well-suggestions`);
  return data;
}

export async function mapWell(batchId: string, fromName: string, toName: string): Promise<void> {
  await apiClient.post(`/data-vault/intake/batches/${batchId}/map-well`, { from_name: fromName, to_name: toName });
}

export async function submitFieldEntry(submission: {
  site_id: string;
  data_type: string;
  rows: Array<{
    well_name: string;
    sample_date: string;
    values: Record<string, string>;
    notes?: string;
    gps_latitude?: number;
    gps_longitude?: number;
  }>;
}): Promise<IntakeBatchSummary> {
  const { data } = await apiClient.post('/data-vault/intake/field-entry', submission);
  return data;
}

export async function learnAnalyteAlias(compound: string, canonicalName: string): Promise<void> {
  await apiClient.post('/data-vault/intake/analyte-aliases', {
    compound,
    canonical_name: canonicalName,
  });
}

// ── EDD Format Templates ────────────────────────────────────────────

export interface EddTemplate {
  id: string;
  name: string;
  lab_name: string | null;
  file_type: string;
  column_mapping: Record<string, string>;
  date_format: string | null;
  skip_rows: number;
  sheet_name: string | null;
  delimiter: string;
  encoding: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export async function listTemplates(labName?: string): Promise<EddTemplate[]> {
  const params: Record<string, string> = {};
  if (labName) params.lab_name = labName;
  const { data } = await apiClient.get('/data-vault/intake/templates', { params });
  return data;
}

export async function getTemplate(id: string): Promise<EddTemplate> {
  const { data } = await apiClient.get(`/data-vault/intake/templates/${id}`);
  return data;
}

export async function createTemplate(
  templateData: Omit<EddTemplate, 'id' | 'created_at' | 'updated_at'>,
): Promise<EddTemplate> {
  const { data } = await apiClient.post('/data-vault/intake/templates', templateData);
  return data;
}

export async function updateTemplate(
  id: string,
  templateData: Partial<EddTemplate>,
): Promise<EddTemplate> {
  const { data } = await apiClient.put(`/data-vault/intake/templates/${id}`, templateData);
  return data;
}

export async function deleteTemplate(id: string): Promise<void> {
  await apiClient.delete(`/data-vault/intake/templates/${id}`);
}

export async function matchTemplates(
  headers: string[],
): Promise<Array<EddTemplate & { score: number }>> {
  const { data } = await apiClient.post('/data-vault/intake/templates/match', { headers });
  return data;
}

// ── Validation Rules ─────────────────────────────────────────────────

export interface ValidationRule {
  id: string;
  client_id: string | null;
  site_id: string | null;
  rule_code: string;
  is_enabled: boolean;
  severity: string;
  parameters: Record<string, unknown>;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface AutoFixSuggestion {
  row_id: string;
  row_number: number;
  field: string;
  current_value: string;
  suggested_value: string;
  rule: string;
  description: string;
  confidence: number;
}

export interface HistoryDataPoint {
  date: string;
  concentration: number;
  is_non_detect: boolean;
}

export interface RowHistoryResponse {
  well_name: string;
  compound: string;
  history: HistoryDataPoint[];
  stats: {
    mean: number;
    std: number;
    n: number;
    max: number;
    min: number;
  };
  current_value: number | null;
}

// ── Validation Rules API ─────────────────────────────────────────────

export async function getValidationRules(siteId?: string): Promise<ValidationRule[]> {
  const params: Record<string, string> = {};
  if (siteId) params.site_id = siteId;
  const { data } = await apiClient.get('/data-vault/intake/validation-rules', { params });
  return data;
}

export async function createValidationRule(rule: {
  rule_code: string;
  site_id?: string;
  is_enabled?: boolean;
  severity?: string;
  parameters?: Record<string, unknown>;
  description?: string;
}): Promise<ValidationRule> {
  const { data } = await apiClient.post('/data-vault/intake/validation-rules', rule);
  return data;
}

export async function updateValidationRule(
  ruleId: string,
  updates: {
    is_enabled?: boolean;
    severity?: string;
    parameters?: Record<string, unknown>;
    description?: string;
  },
): Promise<ValidationRule> {
  const { data } = await apiClient.put(`/data-vault/intake/validation-rules/${ruleId}`, updates);
  return data;
}

export async function deleteValidationRule(ruleId: string): Promise<void> {
  await apiClient.delete(`/data-vault/intake/validation-rules/${ruleId}`);
}

// ── Auto-Fix API ─────────────────────────────────────────────────────

export async function getAutoFixSuggestions(batchId: string): Promise<AutoFixSuggestion[]> {
  const { data } = await apiClient.get(`/data-vault/intake/batches/${batchId}/autofix-suggestions`);
  return data;
}

export async function applyAutoFixes(
  batchId: string,
  fixes: Array<{ row_id: string; field: string; value: string }>,
): Promise<{ applied_count: number; validation_summary: ValidationSummary | null }> {
  const { data } = await apiClient.post(`/data-vault/intake/batches/${batchId}/auto-fix`, { fixes });
  return data;
}

// ── Historical Data API ──────────────────────────────────────────────

export async function getRowHistory(
  batchId: string,
  rowId: string,
): Promise<RowHistoryResponse> {
  const { data } = await apiClient.get(`/data-vault/intake/batches/${batchId}/rows/${rowId}/history`);
  return data;
}

// ── QA/QC Engine Types ──────────────────────────────────────────────

export interface QAQCBatchSummary {
  id: string;
  intake_batch_id: string | null;
  site_id: string;
  validation_level: string;
  status: string;
  total_results: number;
  qualified_count: number;
  rejected_count: number;
  run_by: string | null;
  run_at: string | null;
  results_summary: {
    holding_time?: { checked: number; passed: number; failed: number; details: QCCheckDetail[] };
    method_blanks?: { checked: number; passed: number; failed: number; details: QCCheckDetail[] };
    lcs_recovery?: { checked: number; passed: number; failed: number; details: QCCheckDetail[] };
    ms_msd?: { checked: number; passed: number; failed: number; details: QCCheckDetail[] };
    surrogates?: { checked: number; passed: number; failed: number; details: QCCheckDetail[] };
    field_duplicates?: { checked: number; passed: number; failed: number; details: QCCheckDetail[] };
    trip_blanks?: { checked: number; passed: number; failed: number; details: QCCheckDetail[] };
    equipment_blanks?: { checked: number; passed: number; failed: number; details: QCCheckDetail[] };
    charge_balance?: { checked: number; passed: number; failed: number; details: QCCheckDetail[] };
    completeness?: { checked: number; passed: number; failed: number; details: QCCheckDetail[] };
  };
  created_at: string;
}

export interface QCCheckDetail {
  sample_id?: string;
  analyte?: string;
  method?: string;
  result_value?: number;
  spike_amount?: number;
  recovery_pct?: number;
  rpd_pct?: number;
  control_limit_low?: number;
  control_limit_high?: number;
  passed: boolean;
  qualifier_assigned?: string;
  message?: string;
}

export interface QCSampleResult {
  id: string;
  sample_type: string;
  sample_id: string | null;
  analyte: string | null;
  method: string | null;
  result_value: number | null;
  spike_amount: number | null;
  recovery_pct: number | null;
  rpd_pct: number | null;
  control_limit_low: number | null;
  control_limit_high: number | null;
  passed: boolean | null;
  qualifier_assigned: string | null;
  associated_sample_ids: string[];
  details: Record<string, unknown>;
  created_at: string;
}

export interface QAQCDashboardStats {
  total_results: number;
  usable_count: number;
  usable_pct: number;
  estimated_count: number;
  estimated_pct: number;
  rejected_count: number;
  rejected_pct: number;
  top_issues: Array<{ issue: string; count: number }>;
  recent_batches: Array<{
    id: string;
    file_name: string;
    status: string;
    qualified_count: number;
    rejected_count: number;
    run_at: string;
  }>;
}

// ── QA/QC Engine API ────────────────────────────────────────────────

export async function runQAQC(
  batchId: string,
  validationLevel: string = 'stage_2a',
): Promise<QAQCBatchSummary> {
  const { data } = await apiClient.post(`/data-vault/intake/batches/${batchId}/qaqc`, {
    validation_level: validationLevel,
  });
  return data;
}

export async function getQAQCResults(batchId: string): Promise<QAQCBatchSummary> {
  const { data } = await apiClient.get(`/data-vault/intake/batches/${batchId}/qaqc`);
  return data;
}

export async function getQAQCDetails(
  batchId: string,
  params?: { sample_type?: string; passed?: boolean },
): Promise<QCSampleResult[]> {
  const { data } = await apiClient.get(`/data-vault/intake/batches/${batchId}/qaqc/details`, { params });
  return data;
}

export async function overrideQualifier(
  batchId: string,
  resultIds: string[],
  qualifier: string,
  reason: string,
): Promise<{ updated_count: number }> {
  const { data } = await apiClient.post(`/data-vault/intake/batches/${batchId}/qaqc/override`, {
    result_ids: resultIds,
    qualifier,
    reason,
  });
  return data;
}

export async function getQAQCReport(batchId: string, format: 'html' | 'pdf' = 'html'): Promise<string> {
  if (format === 'pdf') {
    const response = await apiClient.get(`/data-vault/intake/batches/${batchId}/qaqc/report`, {
      params: { format: 'pdf' },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    return url;
  }
  const { data } = await apiClient.get(`/data-vault/intake/batches/${batchId}/qaqc/report`);
  return data;
}

export async function getQAQCDashboard(params?: {
  site_id?: string;
  date_from?: string;
  date_to?: string;
}): Promise<QAQCDashboardStats> {
  const { data } = await apiClient.get('/data-vault/intake/qaqc/dashboard', { params });
  return data;
}
